<template>
    <div
        class="drive-home"
        @dragenter.prevent="handleDragEnter"
        @dragover.prevent
        @dragleave.prevent="handleDragLeave"
        @drop.prevent="handleDrop"
        @paste.prevent="handlePaste"
        @contextmenu.prevent="handleContextMenu"
    >
        <div class="drive-header">
            <a-breadcrumb>
                <a-breadcrumb-item href="#" @click.prevent="navigateToFolder(null)">
                    <a-icon type="home" />
                </a-breadcrumb-item>
                <a-breadcrumb-item
                    v-for="(folder, index) in $_.takeRight(breadcrumbFolders, breadcrumbFolders.length - 1)"
                    :key="folder._id"
                    v-if="breadcrumbFolders.length > 1"
                >
                    <a
                        href="#"
                        @click.prevent="navigateToFolder(folder._id)"
                        v-if="index < breadcrumbFolders.length - 1"
                    >
                        {{ folder.name }}
                    </a>
                    <span v-else>{{ folder.name }}</span>
                </a-breadcrumb-item>
            </a-breadcrumb>
            <h1>
                <a href="#" @click.prevent="navigateToFolder(null)">Drive</a>
                <span v-if="breadcrumbFolders.length > 1">
                    <span v-if="breadcrumbFolders.length > 2"> &gt; </span>
                    <a
                        v-if="breadcrumbFolders.length > 2"
                        href="#"
                        @click.prevent="navigateToFolder(breadcrumbFolders[breadcrumbFolders.length - 2]._id)"
                    >
                        ...
                    </a>
                    <span v-if="breadcrumbFolders.length > 1">
                        &gt; {{ breadcrumbFolders[breadcrumbFolders.length - 1].name }}
                    </span>
                </span>
            </h1>
        </div>

        <div class="drive-toolbar">
            <div class="left-actions">
                <SupsisButton icon="add" primary @click="handleNewFolder">{{ $t("message.newFolder") }}</SupsisButton>
                <SupsisButton icon="upload_file" primary @click="handleNewFile">{{
                    $t("message.newFile")
                }}</SupsisButton>
            </div>
            <div class="right-actions">
                <div class="view-toggle">
                    <SupsisButton v-if="selectedFiles.length > 0" icon="select_all" primary @click="selectAllFiles">
                        {{ $t("chatbotv2.selectAll") }}
                    </SupsisButton>
                    <SupsisButton v-if="selectedFiles.length > 0" icon="delete" danger @click="handleBulkDelete">
                        {{ $t("message.deleteSelected") }} ({{ selectedFiles.length }})
                    </SupsisButton>
                    <SupsisButton
                        icon="grid_view"
                        :ghost="viewMode === 'list'"
                        :primary="viewMode === 'grid'"
                        @click="viewMode = 'grid'"
                    />
                    <SupsisButton
                        icon="list"
                        :ghost="viewMode === 'grid'"
                        :primary="viewMode === 'list'"
                        @click="viewMode = 'list'"
                    />
                </div>
            </div>
        </div>

        <div class="toolbar-divider"></div>

        <div v-if="isLoading" class="loading-spinner">
            <a-spin size="large" />
        </div>

        <FilesCardView
            v-else-if="currentFiles.length > 0 && viewMode === 'grid'"
            :files="currentFiles"
            :selectionMode="isSelectionMode"
            :selectedFiles="selectedFiles"
            @folderDoubleClick="handleFolderDoubleClick"
            @delete="handleDelete"
            @fileSelect="handleFileSelection"
        />
        <FilesListView
            v-else-if="currentFiles.length > 0 && viewMode === 'list'"
            :files="currentFiles"
            :selectionMode="isSelectionMode"
            :selectedFiles="selectedFiles"
            @folderDoubleClick="handleFolderDoubleClick"
            @delete="handleDelete"
            @fileSelect="handleFileSelection"
        />

        <div v-else class="empty-folder-placeholder">
            <i class="material-icons">folder_open</i>
            <p>{{ $t("message.noFilesFound") }}</p>
            <SupsisButton icon="upload_file" primary @click="handleNewFile">
                {{ $t("message.uploadFile") }}</SupsisButton
            >
        </div>

        <!-- Upload Progress -->
        <div v-if="showUploadProgress" class="upload-progress">
            <div v-for="upload in activeUploads" :key="upload.id" class="upload-item">
                <span class="filename">{{ upload.fileName }}</span>
                <md-progress-bar md-mode="determinate" :md-value="upload.progress"></md-progress-bar>
            </div>
        </div>

        <!-- Yeni Klasör Modal -->
        <Modal2
            v-if="showNewFolderModal"
            :title="$t('message.newFolder')"
            :description="$t('message.enterFolderName')"
            :okText="$t('settings.create')"
            :cancelText="$t('message.cancel')"
            @ok="createNewFolder"
            @cancel="showNewFolderModal = false"
        >
            <div class="folder-name-input">
                <a-input
                    v-model="newFolderName"
                    :placeholder="$t('message.folderName')"
                    @keyup.enter="createNewFolder"
                    ref="folderNameInput"
                />
                <span v-if="folderNameError" class="error-text">{{ folderNameError }}</span>
            </div>
        </Modal2>

        <div v-if="isDragging" class="drop-overlay">
            <div class="drop-target">
                <i class="fas fa-cloud-upload-alt"></i>
                <p>{{ $t("message.dropFileHere") }}</p>
            </div>
        </div>

        <div v-if="showContextMenu" class="context-menu" :style="contextMenuStyle">
            <div class="context-menu-title">{{ $t("message.actions") }}</div>
            <div class="context-menu-item" @click="handleNewFile">
                <i class="fas fa-file-upload text-primary"></i> {{ $t("message.uploadFile") }}
            </div>
            <div class="context-menu-item" @click="handleNewFolder">
                <i class="fas fa-folder-plus text-success"></i> {{ $t("message.createFolder") }}
            </div>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import FilesCardView from "./FilesCardView.vue";
import FilesListView from "./FilesListView.vue";
import { DRIVE_API } from "../../../api/drive";
import SupsisButton from "../Settings/components/SupsisButton.vue";
import { uploadAsset } from "../../../api/assetUpload";
import axios from "axios";
import log2 from "../../../utils/log2";
import { mapState } from "vuex";
import Modal2 from "../../../components/common/Modal2.vue";

const log = log2("Drive");
export default Vue.extend({
    name: "DriveHome",
    components: {
        FilesCardView,
        SupsisButton,
        Modal2,
        FilesListView,
    },
    data() {
        return {
            folders: [],
            currentFolderId: null,
            currentFiles: [],
            viewMode: "grid",
            activeUploads: [],
            showUploadProgress: false,
            showNewFolderModal: false,
            newFolderName: "",
            folderNameError: "",
            breadcrumbFolders: [],
            isDragging: false,
            dragCounter: 0,
            showContextMenu: false,
            contextMenuPosition: {
                x: 0,
                y: 0,
            },
            isLoading: false,
            selectedFiles: [],
            isSelectionMode: false,
        };
    },
    computed: {
        ...mapState({
            site: (state) => state.site.site,
        }),
        contextMenuStyle() {
            return {
                top: `${this.contextMenuPosition.y}px`,
                left: `${this.contextMenuPosition.x}px`,
            };
        },
        currentFolderName() {
            if (this.currentFolderId) {
                const currentFolder = this.folders.find((f) => f._id === this.currentFolderId);
                return currentFolder ? currentFolder.name : "";
            } else {
                return "Drive";
            }
        },
    },
    async created() {
        await this.fetchFolders();
        if (this.folders.length > 0) {
            const rootFolder = this.folders.find((f) => f.parent === null);
            if (rootFolder) {
                this.currentFolderId = rootFolder._id;
                await this.fetchCurrentFolderFiles();
            }
        }
    },
    methods: {
        viewFolder(folderId) {},
        showRootFolder() {
            const root = this.folders.find((f) => f.parent === null);
            if (root) {
                this.currentFolderId = root._id;
            }
        },
        async fetchFolders() {
            try {
                this.folders = await DRIVE_API.listAllFolders();
                if (!this.currentFolderId) this.showRootFolder();
                this.updateBreadcrumb();
            } catch (error) {
                console.error("Klasörler yüklenirken hata:", error);
            }
        },
        updateBreadcrumb() {
            const currentFolder = this.folders.find((f) => f._id === this.currentFolderId);
            const breadcrumbFolders = [];

            let folder = currentFolder;
            while (folder) {
                breadcrumbFolders.unshift(folder);
                folder = this.folders.find((f) => f._id === folder.parent);
            }

            this.breadcrumbFolders = breadcrumbFolders;
        },
        navigateToFolder(folderId) {
            if (folderId === null) {
                this.showRootFolder();
            } else {
                this.currentFolderId = folderId;
            }
        },
        async fetchCurrentFolderFiles() {
            if (!this.currentFolderId) return;
            this.isLoading = true;
            try {
                this.currentFiles = await DRIVE_API.listFilesByFolderId(this.currentFolderId);
            } catch (error) {
                console.error("Dosyalar yüklenirken hata:", error);
            } finally {
                this.isLoading = false;
            }
        },
        async handleFolderDoubleClick(folderId) {
            log(`[DRIVE] Folder double clicked: ${folderId}`);
            this.currentFolderId = folderId;
        },
        handleNewFolder() {
            this.showNewFolderModal = true;
            this.newFolderName = "";
            this.folderNameError = "";
            // Input'a fokuslanmak için nextTick kullanıyoruz
            this.$nextTick(() => {
                this.$refs.folderNameInput?.focus();
            });
        },
        async handleNewFile() {
            const input = document.createElement("input");
            input.type = "file";
            input.multiple = true;

            input.onchange = async (e) => {
                const files = Array.from(e.target.files);
                this.showUploadProgress = true;

                for (const file of files) {
                    const uploadId = Math.random().toString(36);
                    this.activeUploads.push({
                        id: uploadId,
                        fileName: file.name,
                        progress: 0,
                    });

                    await this.uploadFile(file, uploadId);
                }
                this.showUploadProgress = false;
            };

            input.click();
        },
        async uploadFile(file, uploadId) {
            try {
                // Asset oluştur
                const path = `${this.site._id}/drive/${this.currentFolderId}`;
                const { data: assetData } = await uploadAsset({
                    path,
                    mime: file.type,
                    name: file.name,
                    ext: file.name.split(".").pop(),
                    size: file.size,
                    type: "drive",
                });
                log("Asset oluşturuldu", assetData);

                // S3'e yükle
                await axios({
                    method: assetData.method,
                    url: assetData.url,
                    data: file,
                    headers: { "Content-Type": file.type },
                    onUploadProgress: (progressEvent) => {
                        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);

                        const uploadIndex = this.activeUploads.findIndex((u) => u.id === uploadId);
                        if (uploadIndex !== -1) {
                            this.activeUploads[uploadIndex].progress = progress;
                        }
                    },
                });

                // Drive API'ye bildir
                await DRIVE_API.addAssetToFolder(this.currentFolderId, assetData.asset._id);

                // Upload listesinden kaldır
                this.activeUploads = this.activeUploads.filter((u) => u.id !== uploadId);
                if (this.activeUploads.length === 0) {
                    this.showUploadProgress = false;
                }

                // Dosya listesini güncelle
                await this.fetchCurrentFolderFiles();
            } catch (error) {
                console.error("Dosya yükleme hatası:", error);
                this.$message.error(this.$t("message.fileUploadingError"));

                const uploadIndex = this.activeUploads.findIndex((u) => u.id === uploadId);
                if (uploadIndex !== -1) {
                    this.activeUploads[uploadIndex].error = true;
                }
            }
        },
        async createNewFolder() {
            if (!this.newFolderName.trim()) {
                this.folderNameError = "Klasör adı boş olamaz";
                return;
            }

            try {
                // Yeni klasör oluştur
                await DRIVE_API.createFolder(this.newFolderName.trim(), this.currentFolderId);

                // Klasör listesini güncelle
                await this.fetchFolders();

                // Mevcut klasörün içeriğini güncelle
                await this.fetchCurrentFolderFiles();

                // Modalı kapat
                this.showNewFolderModal = false;

                // Başarı mesajı göster
                this.$message.success(this.$t("message.fileUploadingSucs"));
            } catch (error) {
                console.error("Klasör oluşturma hatası:", error);
                this.folderNameError = "Klasör oluşturulurken bir hata oluştu";
            }
        },
        handleDragEnter(event) {
            event.preventDefault();
            this.dragCounter++;
            if (this.dragCounter === 1) {
                this.isDragging = true;
            }
        },
        handleDragLeave(event) {
            event.preventDefault();
            this.dragCounter--;
            if (this.dragCounter === 0) {
                this.isDragging = false;
            }
        },
        async handleDrop(event) {
            event.preventDefault();
            this.isDragging = false;
            this.dragCounter = 0;

            const files = Array.from(event.dataTransfer.files);
            log("Dosyalar sürüklendi:", files);
            await this.uploadFiles(files);
        },
        async uploadFiles(files) {
            this.showUploadProgress = true;

            for (const file of files) {
                const uploadId = Math.random().toString(36);
                this.activeUploads.push({
                    id: uploadId,
                    fileName: file.name,
                    progress: 0,
                });

                await this.uploadFile(file, uploadId);
            }
            this.showUploadProgress = false;
        },
        handleContextMenu(event) {
            if (!event.target.closest(".file-card")) {
                event.preventDefault();
                this.showContextMenu = true;
                this.contextMenuPosition = {
                    x: event.clientX,
                    y: event.clientY,
                };
            }
        },
        hideContextMenu() {
            this.showContextMenu = false;
        },
        async handlePaste(event) {
            const items = event.clipboardData.items;
            const files = [];

            for (const item of items) {
                if (item.kind === "file") {
                    const file = item.getAsFile();
                    files.push(file);
                }
            }

            if (files.length > 0) {
                await this.uploadFiles(files);
            }
        },
        async handleDelete(file) {
            try {
                if (file.isFolder) {
                    await this.$confirm({
                        title: this.$t("message.deleteFolder"),
                        content: this.$t("message.sureDeleteFolder"),
                        okText: this.$t("message.yes"),
                        okType: "danger",
                        cancelText: this.$t("message.no"),
                        onOk: async () => {
                            await DRIVE_API.deleteFolder(file._id);
                            this.$message.success(this.$t("message.deletionSucs"));
                            await this.fetchCurrentFolderFiles();
                        },
                    });
                } else {
                    await this.$confirm({
                        title: this.$t("message.deleteFile"),
                        content: this.$t("message.sureDeleteFile"),
                        okText: this.$t("message.yes"),
                        okType: "danger",
                        cancelText: this.$t("message.no"),
                        onOk: async () => {
                            await DRIVE_API.removeMultiFiles(this.currentFolderId, [file._id]);
                            this.$message.success(this.$t("message.deletionSucs"));
                            await this.fetchCurrentFolderFiles();
                        },
                    });
                }
            } catch (error) {
                if (error.message !== "cancel") {
                    console.error("Silme hatası:", error);
                    this.$message.error(this.$t("message.deletionError"));
                }
            }
        },
        toggleSelectionMode() {
            this.isSelectionMode = !this.isSelectionMode;
            if (!this.isSelectionMode) {
                this.selectedFiles = [];
            }
        },
        handleFileSelection(file) {
            const index = this.selectedFiles.findIndex((f) => f._id === file._id);
            if (index !== -1) {
                this.selectedFiles.splice(index, 1);
                if (this.selectedFiles.length === 0) {
                    this.isSelectionMode = false;
                }
            } else {
                this.selectedFiles.push(file);
                this.isSelectionMode = true;
            }
        },
        async handleBulkDelete() {
            try {
                await this.$confirm({
                    title: this.$t("message.deleteFiles"),
                    content: this.$t("message.sureDeleteMultipleFiles", { count: this.selectedFiles.length }),
                    okText: this.$t("message.yes"),
                    okType: "danger",
                    cancelText: this.$t("message.no"),
                    onOk: async () => {
                        const fileIds = this.selectedFiles.filter((f) => !f.isFolder).map((f) => f._id);
                        const folderIds = this.selectedFiles.filter((f) => f.isFolder).map((f) => f._id);

                        if (fileIds.length > 0) {
                            await DRIVE_API.removeMultiFiles(this.currentFolderId, fileIds);
                        }

                        if (folderIds.length > 0) {
                            for (const folderId of folderIds) {
                                await DRIVE_API.deleteFolder(folderId);
                            }
                        }

                        this.$message.success(this.$t("message.deletionSucs"));
                        this.selectedFiles = [];
                        this.isSelectionMode = false;
                        await this.fetchCurrentFolderFiles();
                    },
                });
            } catch (error) {
                if (error.message !== "cancel") {
                    console.error("Toplu silme hatası:", error);
                    this.$message.error(this.$t("message.deletionError"));
                }
            }
        },
        selectAllFiles() {
            // Eğer tüm dosyalar zaten seçiliyse, seçimi kaldır
            if (this.selectedFiles.length === this.currentFiles.length) {
                this.selectedFiles = [];
                this.isSelectionMode = false;
            } else {
                // Tüm dosyaları seç
                this.selectedFiles = [...this.currentFiles];
                this.isSelectionMode = true;
            }
        },
    },
    watch: {
        currentFolderId() {
            this.fetchCurrentFolderFiles();
            this.updateBreadcrumb();
        },
    },
    mounted() {
        document.addEventListener("click", this.hideContextMenu);
    },
    beforeDestroy() {
        document.removeEventListener("click", this.hideContextMenu);
    },
});
</script>

<style scoped lang="scss">
.drive-home {
    height: 100%;
}

.drive-header {
    margin-bottom: 20px;
    background-color: #f0f2f5;
    padding: 16px;
    border-radius: 4px;

    .ant-breadcrumb {
        font-size: 18px;
        margin-bottom: 8px;

        .anticon {
            margin-right: 8px;
        }
    }

    h1 {
        font-size: 24px;
        font-weight: 600;
        color: var(--color-text-default);
        margin: 0;
    }
}

.drive-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding: 10px;

    .left-actions {
        display: flex;
        gap: 10px;
    }

    .right-actions {
        display: flex;
        align-items: center;
        gap: 10px;

        .view-toggle {
            display: flex;
            align-items: center;
            gap: 10px;
        }
    }
}

.toolbar-divider {
    width: 100%;
    height: 1px;
    background-color: #e0e0e0;
    margin-bottom: 20px;
}

.empty-folder-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px;
    margin: 20px;
    border: 2px dashed #ccc;
    border-radius: 8px;
    background-color: #fff;

    i.material-icons {
        font-size: 48px;
        color: #666;
        margin-bottom: 16px;
    }

    p {
        color: #666;
        font-size: 16px;
        margin-bottom: 20px;
    }
}

.upload-progress {
    position: fixed;
    bottom: 20px;
    right: 20px;
    background: white;
    padding: 15px;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    min-width: 300px;
    z-index: 1000;

    .upload-item {
        margin-bottom: 10px;

        &:last-child {
            margin-bottom: 0;
        }

        .filename {
            display: block;
            margin-bottom: 5px;
            font-size: 14px;
            color: #666;
        }
    }
}

.folder-name-input {
    display: flex;
    flex-direction: column;
    gap: 8px;

    .error-text {
        color: #ff4d4f;
        font-size: 12px;
    }
}

.drive-breadcrumb {
    margin-bottom: 20px;
}

.drop-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
}

.drop-target {
    background-color: #fff;
    padding: 30px;
    border-radius: 5px;
    text-align: center;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

    i {
        font-size: 3rem;
        color: #007bff;
        margin-bottom: 10px;
    }

    p {
        font-size: 1.2rem;
        color: #333;
    }
}

.context-menu {
    position: fixed;
    background-color: white;
    border-radius: 4px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    padding: 8px 0;
    z-index: 1000;

    .context-menu-title {
        font-weight: bold;
        padding: 8px 16px;
        border-bottom: 1px solid #e0e0e0;
    }

    .context-menu-item {
        display: flex;
        align-items: center;
        padding: 8px 16px;
        cursor: pointer;

        &:hover {
            background-color: #f0f0f0;
        }

        i {
            margin-right: 8px;
        }

        .text-primary {
            color: #007bff;
        }

        .text-success {
            color: #28a745;
        }
    }
}

.loading-spinner {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
}
</style>
